import React, { useState } from 'react';
import { useAuth } from '@/providers/AuthProvider';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';

export const NotificationTest: React.FC = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [notificationType, setNotificationType] = useState('system');
  const [title, setTitle] = useState('Test Notification');
  const [message, setMessage] = useState('This is a test notification');

  const createTestNotification = async () => {
    if (!user) {
      toast({
        title: 'Error',
        description: 'User not authenticated',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    try {
      // Get user's organization
      const { data: userOrg, error: userOrgError } = await supabase
        .from('user_organization')
        .select('organization_uuid')
        .eq('user_uuid', user.id)
        .single();

      if (userOrgError || !userOrg) {
        throw new Error('Could not find user organization');
      }

      // Create notification
      const { data, error } = await supabase.rpc('create_notification', {
        p_user_id: user.id,
        p_organization_uuid: userOrg.organization_uuid,
        p_type: notificationType,
        p_title: title,
        p_message: message,
        p_data: {
          test: true,
          timestamp: new Date().toISOString(),
          type: notificationType
        }
      });

      if (error) {
        throw error;
      }

      toast({
        title: 'Success',
        description: 'Test notification created successfully!',
      });

      // Clear form
      setTitle('Test Notification');
      setMessage('This is a test notification');
    } catch (error) {
      console.error('Error creating notification:', error);
      toast({
        title: 'Error',
        description: 'Failed to create notification',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const createQuickNotifications = async () => {
    if (!user) return;

    setLoading(true);
    try {
      // Get user's organization
      const { data: userOrg, error: userOrgError } = await supabase
        .from('user_organization')
        .select('organization_uuid')
        .eq('user_uuid', user.id)
        .single();

      if (userOrgError || !userOrg) {
        throw new Error('Could not find user organization');
      }

      const notifications = [
        {
          type: 'system',
          title: 'System Maintenance',
          message: 'Scheduled maintenance will occur tonight at 2 AM.'
        },
        {
          type: 'new_application',
          title: 'New Application: Frontend Developer',
          message: 'John Doe has applied to the Frontend Developer position.'
        },
        {
          type: 'application_status_change',
          title: 'Application Status Changed',
          message: 'Application for Senior Developer moved to Interview stage.'
        },
        {
          type: 'candidate_update',
          title: 'Candidate Profile Updated',
          message: 'Sarah Johnson\'s profile has been updated with new skills.'
        },
        {
          type: 'comment_mention',
          title: 'You were mentioned in a comment',
          message: 'Kevin mentioned you in a comment on the Senior Developer job.'
        }
      ];

      for (const notification of notifications) {
        await supabase.rpc('create_notification', {
          p_user_id: user.id,
          p_organization_uuid: userOrg.organization_uuid,
          p_type: notification.type,
          p_title: notification.title,
          p_message: notification.message,
          p_data: {
            test: true,
            timestamp: new Date().toISOString(),
            type: notification.type
          }
        });
      }

      toast({
        title: 'Success',
        description: 'Created 5 test notifications!',
      });
    } catch (error) {
      console.error('Error creating notifications:', error);
      toast({
        title: 'Error',
        description: 'Failed to create notifications',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto p-6 max-w-2xl">
      <Card>
        <CardHeader>
          <CardTitle>Notification System Test</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="type">Notification Type</Label>
            <Select value={notificationType} onValueChange={setNotificationType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="system">System</SelectItem>
                <SelectItem value="new_application">New Application</SelectItem>
                <SelectItem value="application_status_change">Application Status Change</SelectItem>
                <SelectItem value="candidate_update">Candidate Update</SelectItem>
                <SelectItem value="comment_mention">Comment Mention</SelectItem>
                <SelectItem value="job_update">Job Update</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Notification title"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="message">Message</Label>
            <Textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Notification message"
              rows={3}
            />
          </div>

          <div className="flex gap-2">
            <Button 
              onClick={createTestNotification} 
              disabled={loading}
              className="flex-1"
            >
              {loading ? 'Creating...' : 'Create Test Notification'}
            </Button>
            
            <Button 
              onClick={createQuickNotifications} 
              disabled={loading}
              variant="outline"
            >
              {loading ? 'Creating...' : 'Create 5 Quick Notifications'}
            </Button>
          </div>

          <div className="text-sm text-muted-foreground">
            <p>• Check the notification bell in the header to see notifications</p>
            <p>• Notifications will appear in real-time</p>
            <p>• You can mark them as read by clicking on them</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}; 